import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader } from '@/components/Shared';
import { UserCog, Plus, Pencil, AlertTriangle, Trash2, Eraser } from 'lucide-react';
import { shortDate } from '@/lib/format';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth';
import { DangerConfirm } from '@/components/DangerConfirm';

const EMPTY = { name: '', email: '', phone: '', password: '', role: 'employee' };

const CLEANUP_TARGETS = [
  { key: 'customers', label: 'All Customers', desc: 'Deletes every customer except the default walk-in customer.' },
  { key: 'employees', label: 'All Employees', desc: 'Deletes every employee account (admins are kept).' },
  { key: 'invoices', label: 'All Invoices', desc: 'Deletes all invoices and credit notes.' },
  { key: 'payments', label: 'All Payments', desc: 'Deletes all payment and refund records.' },
  { key: 'requests', label: 'All Requests', desc: 'Deletes all buy and return requests.' },
  { key: 'notifications', label: 'All Notifications', desc: 'Deletes all notifications.' },
  { key: 'commissions', label: 'All Commissions', desc: 'Resets the commission on every invoice to $0.' },
  { key: 'returns', label: 'All Returns & Refunds', desc: 'Deletes all return credit notes and refund records.' },
];

export default function Users() {
  const { logout, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  // Full factory reset
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPw, setResetPw] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetting, setResetting] = useState(false);

  // Delete (archive) employee
  const [delTarget, setDelTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Granular cleanup tool
  const [cleanup, setCleanup] = useState({ open: false, target: null, label: '', count: null, pw: '', confirm: '', busy: false });

  const load = useCallback(async () => { const { data } = await api.get('/users'); setUsers(data); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, phone: u.phone || '', password: '', role: u.role }); setOpen(true); };

  const doReset = async () => {
    if (resetConfirm.trim().toUpperCase() !== 'RESET') { toast.error('Type RESET to confirm'); return; }
    if (!resetPw) { toast.error('Enter your admin password'); return; }
    setResetting(true);
    try {
      const { data } = await api.post('/factory-reset', { password: resetPw });
      const deleted = data?.deleted || {};
      const summary = Object.entries(deleted).filter(([, n]) => n > 0).map(([k, n]) => `${k}: ${n}`).join(', ') || 'no existing records';
      toast.success(`Cleared ${data?.total_deleted ?? 0} records (${summary}). Signing you out…`, { duration: 6000 });
      setResetOpen(false);
      setTimeout(() => logout(), 2500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reset failed');
    } finally { setResetting(false); }
  };

  const save = async () => {
    if (!form.name || !form.email || (!editing && !form.password)) { toast.error('Name, email and password are required'); return; }
    try {
      if (editing) {
        const payload = { name: form.name, phone: form.phone, role: form.role };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.id}`, payload); toast.success('User updated');
      } else {
        await api.post('/users', form); toast.success('User created');
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const toggleActive = async (u) => {
    try { await api.put(`/users/${u.id}`, { active: !u.active }); toast.success(u.active ? 'User deactivated' : 'User activated'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const doDelete = async (password) => {
    if (!delTarget) return;
    setDeleting(true);
    try {
      await api.post(`/users/${delTarget.id}/delete`, { password, confirm: 'CONFIRM' });
      toast.success(`${delTarget.name} deleted. Login disabled; history kept.`);
      setDelTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setDeleting(false); }
  };

  const [purgeTarget, setPurgeTarget] = useState(null);
  const [purging, setPurging] = useState(false);
  const doPurge = async (password) => {
    setPurging(true);
    try {
      await api.post(`/users/${purgeTarget.id}/purge`, { password, confirm: 'CONFIRM' });
      toast.success(`${purgeTarget.name} permanently deleted.`);
      setPurgeTarget(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setPurging(false); }
  };
  const openCleanup = async (t) => {
    setCleanup({ open: true, target: t.key, label: t.label, count: null, pw: '', confirm: '', busy: false, desc: t.desc });
    try {
      const { data } = await api.get(`/cleanup/${t.key}/count`);
      setCleanup((c) => ({ ...c, count: data.count }));
    } catch { setCleanup((c) => ({ ...c, count: 0 })); }
  };

  const doCleanup = async () => {
    if (!cleanup.pw) { toast.error('Enter your admin password'); return; }
    if ((cleanup.confirm || '').trim().toUpperCase() !== 'CONFIRM') { toast.error('Type CONFIRM to proceed'); return; }
    setCleanup((c) => ({ ...c, busy: true }));
    try {
      const { data } = await api.post(`/cleanup/${cleanup.target}`, { password: cleanup.pw, confirm: cleanup.confirm });
      toast.success(`Deleted ${data.deleted} record(s) — ${data.label}.`, { duration: 5000 });
      setCleanup({ open: false, target: null, label: '', count: null, pw: '', confirm: '', busy: false });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Cleanup failed');
      setCleanup((c) => ({ ...c, busy: false }));
    }
  };

  return (
    <div>
      <PageHeader title="Users" subtitle="Only admins can create employee accounts — there is no public signup." testid="users-header"
        actions={<Button onClick={openNew} data-testid="users-add-button"><Plus className="h-4 w-4 mr-2" /> Add User</Button>} />
      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid="user-row" className={u.deleted ? 'opacity-60' : ''}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>{u.phone || '-'}</TableCell>
                  <TableCell><span className="capitalize inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]">{u.role}</span></TableCell>
                  <TableCell>
                    {u.deleted ? (
                      <span className="inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700" data-testid="user-deleted-badge">Deleted</span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Switch checked={!!u.active} onCheckedChange={() => toggleActive(u)} data-testid="user-active-toggle" />
                        <span className={`text-sm ${u.active ? 'text-emerald-600' : 'text-red-600'}`}>{u.active ? 'Active' : 'Inactive'}</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{shortDate(u.created_at)}</TableCell>
                  <TableCell>
                    {!u.deleted ? (
                      <div className="flex items-center gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEdit(u)} data-testid="user-edit-button"><Pencil className="h-4 w-4" /></Button>
                        {u.id !== currentUser?.id && (
                          <Button variant="ghost" size="icon" onClick={() => setDelTarget(u)} data-testid="user-delete-button"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                        )}
                      </div>
                    ) : (
                      u.id !== currentUser?.id && (
                        <Button variant="ghost" size="sm" className="text-red-600" onClick={() => setPurgeTarget(u)} data-testid="user-purge-button"><Trash2 className="h-4 w-4 mr-1" /> Delete permanently</Button>
                      )
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit User' : 'Add User'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" className="mt-1.5" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} data-testid="user-email-input" className="mt-1.5" /></div>
            <div><Label>Phone Number</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-phone-input" className="mt-1.5" /></div>
            <div><Label>{editing ? 'New Password (optional)' : 'Password'}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" className="mt-1.5" /></div>
            <div><Label>Role</Label><Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger className="mt-1.5" data-testid="user-role-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="employee">Employee</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="user-save-button">{editing ? 'Save Changes' : 'Create User'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete (archive) employee confirmation */}
      <DangerConfirm
        open={!!delTarget}
        onOpenChange={(o) => !o && setDelTarget(null)}
        title={`Delete ${delTarget?.name || 'employee'}?`}
        description="This blocks the account from logging in and marks it as Deleted. Their historical invoices and linked customer record are kept. No products, inventory or invoices are deleted."
        affectedText={delTarget ? `Archives employee ${delTarget.name} (${delTarget.email}). Login disabled immediately.` : ''}
        onConfirm={doDelete}
        busy={deleting}
        confirmLabel="Delete Employee"
      />

      <DangerConfirm
        open={!!purgeTarget}
        onOpenChange={(o) => !o && setPurgeTarget(null)}
        title={`Permanently delete ${purgeTarget?.name || 'employee'}?`}
        description="This permanently removes the employee account. Historical invoices keep the stored name. This is irreversible."
        affectedText={purgeTarget ? `Permanently deletes ${purgeTarget.name} (${purgeTarget.email}).` : ''}
        onConfirm={doPurge}
        busy={purging}
        confirmLabel="Delete Permanently"
      />

      {/* Danger zone: granular cleanup tools */}
      <Card className="mt-8 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/[0.03] p-6" data-testid="cleanup-zone-card">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"><Eraser className="h-5 w-5" /></span>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-[hsl(var(--destructive))]">Data Cleanup Tools</h3>
            <p className="mt-1 text-sm text-muted-foreground max-w-2xl">Delete specific data sets individually. Each action requires your password and typing <b>CONFIRM</b>. <b>Products and inventory are never touched</b> by these tools.</p>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {CLEANUP_TARGETS.map((t) => (
                <Button key={t.key} variant="outline" className="justify-start border-[hsl(var(--destructive))]/30 text-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/5" onClick={() => openCleanup(t)} data-testid={`cleanup-open-${t.key}`}>
                  <Trash2 className="h-4 w-4 mr-2 shrink-0" /> {t.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* Cleanup confirm dialog */}
      <Dialog open={cleanup.open} onOpenChange={(o) => !o && setCleanup((c) => ({ ...c, open: false }))}>
        <DialogContent className="max-w-md" data-testid="cleanup-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2 text-[hsl(var(--destructive))]"><AlertTriangle className="h-5 w-5" /> Delete {cleanup.label}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">{cleanup.desc}</p>
            <div className="rounded-lg border bg-[hsl(var(--secondary))] p-3 text-sm">
              This will affect <b data-testid="cleanup-count">{cleanup.count === null ? '…' : cleanup.count}</b> record(s). This action cannot be undone.
            </div>
            <div><Label>Confirm your admin password</Label><Input type="password" value={cleanup.pw} onChange={(e) => setCleanup((c) => ({ ...c, pw: e.target.value }))} data-testid="cleanup-password-input" className="mt-1.5" /></div>
            <div><Label>Type <b>CONFIRM</b> to proceed</Label><Input value={cleanup.confirm} onChange={(e) => setCleanup((c) => ({ ...c, confirm: e.target.value }))} placeholder="CONFIRM" data-testid="cleanup-confirm-input" className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCleanup((c) => ({ ...c, open: false }))}>Cancel</Button>
            <Button variant="destructive" onClick={doCleanup} disabled={cleanup.busy || cleanup.count === 0} data-testid="cleanup-confirm-button">{cleanup.busy ? 'Deleting…' : `Delete ${cleanup.count ?? ''} record(s)`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Danger zone: full data reset */}
      <Card className="mt-6 border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/[0.03] p-6" data-testid="danger-zone-card">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]"><AlertTriangle className="h-5 w-5" /></span>
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-[hsl(var(--destructive))]">Factory Reset (Everything)</h3>
            <p className="mt-1 text-sm text-muted-foreground max-w-xl">
              Permanently delete <b>all</b> products, inventory, customers, invoices, payments, purchases,
              requests, notifications and every other user. Only your admin account is kept. This cannot be undone.
            </p>
            <Button variant="destructive" className="mt-4" onClick={() => { setResetPw(''); setResetConfirm(''); setResetOpen(true); }} data-testid="reset-data-open-button">
              <Trash2 className="h-4 w-4 mr-2" /> Reset all data
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="max-w-md" data-testid="reset-data-dialog">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2 text-[hsl(var(--destructive))]"><AlertTriangle className="h-5 w-5" /> Reset all data</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">This will permanently erase every product, customer, invoice, payment, purchase, request and user except your admin account. This action is irreversible.</p>
            <div><Label>Confirm your admin password</Label><Input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid="reset-data-password-input" className="mt-1.5" /></div>
            <div><Label>Type <b>RESET</b> to confirm</Label><Input value={resetConfirm} onChange={(e) => setResetConfirm(e.target.value)} placeholder="RESET" data-testid="reset-data-confirm-input" className="mt-1.5" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doReset} disabled={resetting} data-testid="reset-data-confirm-button">{resetting ? 'Resetting…' : 'Erase everything'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
